import { yupResolver } from '@hookform/resolvers/yup';
import { debounce } from 'lodash';
import type { ChangeEvent, FormEventHandler, Key, Ref } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Observable, Subject, takeUntil } from 'rxjs';
import * as yup from 'yup';
import type {
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataUpdate,
} from '../../../common/data-retrieval/data-retrieval-interfaces';
import { OdinDataSender } from '../../../common/data-retrieval/odin-data-sender';
import type { ModalDialogButtonSetting } from '../../common-interfaces';
import { DynamicFormRequiredType } from '../../dynamic-form/dynamic-form.enums';
import HfInput from '../../fields/input/hf-input.component';
import type { JsonData, JsonDataItem } from '../../generic-types';
import type { DialogProps, DialogRef } from '../../modal-dialog/dialog.component';
import Dialog from '../../modal-dialog/dialog.component';

export interface AddNewModalProps extends Omit<DialogProps, 'children' | 'buttons'> {
  ref?: Ref<DialogRef>;
  formId?: string;
  fieldId?: Key;
  textField: string;
  valueField: string;
  placeholder?: string;
  data?: JsonData | (OdinDataRetrieval & OdinDataUpdate<JsonDataWrapper>);
  defaultValue?: string;
  buttonText?: string;
  helpText?: string;
  onItemCreated?: (value: JsonDataItem) => void;
}

function AddNewModal(props: AddNewModalProps) {
  const modalProps: Omit<DialogProps, 'children' | 'buttons'> = props;
  const {
    ref,
    formId = 'addNewModal',
    textField,
    valueField,
    data,
    defaultValue,
    fieldId,
    buttonText,
    placeholder,
    helpText,
    visible,
    onItemCreated,
  } = props;
  const { t } = useTranslation();

  const [button, setButton] = useState<ModalDialogButtonSetting>({
    name: 'formSubmitButton',
    text: buttonText ?? t('apply'),
    type: 'primary',
    variant: 'alternative',
    form: formId,
  });

  const dataResultSender = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const dataCreateSender = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const destroySubject = useRef<Subject<void> | undefined>(undefined);

  const formSchema = yup.object().shape({
    value: yup.string().required(t('insert-value')),
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitted },
    setFocus,
    setError,
    clearErrors,
    reset,
  } = useForm<FieldValues>({
    resolver: yupResolver(formSchema) as never,
    mode: 'all',
    defaultValues: {
      value: '',
    },
  });

  useEffect(() => {
    if (visible) {
      reset({ value: defaultValue ?? '' });
    }
  }, [visible, defaultValue, reset]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Mount-only effect: setError, clearErrors (from useForm), t (i18next), and onItemCreated are stable
  useEffect(() => {
    destroySubject.current = new Subject<void>();

    new Observable<JsonDataWrapper>((subscriber) => {
      dataResultSender.current = new OdinDataSender(subscriber);
    })
      .pipe(takeUntil(destroySubject.current))
      .subscribe({
        next: (dataWrapper) => {
          if (dataWrapper?.data) {
            if (dataWrapper.data.length === 0) {
              clearErrors();
            } else if (dataWrapper.data[0].error) {
              setError('value', {
                message: dataWrapper.data[0].error as string,
              });
            } else {
              setError('value', {
                message: t('value-exists'),
              });
            }
          } else if (dataWrapper?.customProperties?.isError) {
            setError('value', {
              message: dataWrapper.customProperties?.error as string,
            });
          }
        },
        error: (error) => {
          const details = error?.data?.validation?.errors[0] ?? '';
          setError('value', {
            message: `There was unexpected error ${details}`,
          });
        },
      });

    new Observable<JsonDataWrapper>((subscriber) => {
      dataCreateSender.current = new OdinDataSender(subscriber);
    })
      .pipe(takeUntil(destroySubject.current))
      .subscribe({
        next: (dataWrapper) => {
          const returnValue = dataWrapper.data?.[0];
          if (dataWrapper.customProperties?.isError) {
            console.error(`There was an error creating new item ${dataWrapper.customProperties.error}`);
          } else if (returnValue) {
            onItemCreated?.(returnValue);
          }
        },
        error: (error) => console.error(error),
      });

    return () => {
      destroySubject.current?.next();
      destroySubject.current?.complete();
    };
  }, []);

  useEffect(() => {
    setButton((state) => ({ ...state, disabled: !isValid }));
  }, [isValid]);

  const onFocus = () => {
    setFocus('value');
  };

  const onChangeEvent = debounce((event: ChangeEvent) => {
    const newData = (event.target as HTMLInputElement).value;
    if (newData.length !== 0) {
      if (data && !Array.isArray(data) && dataResultSender.current) {
        data.getData(dataResultSender.current, {
          fieldId: fieldId,
          filter: newData,
          fieldType: 'CUSTOM',
          customFieldType: 'ADDNEWMODAL',
        });
      }
    }
  }, 700);

  const addValue = (modalData: string) => {
    const addedValue: JsonDataItem = {
      [valueField]: modalData,
      [textField]: modalData,
    };

    if (data && !Array.isArray(data) && data.saveData && dataCreateSender.current) {
      data.saveData(addedValue, dataCreateSender.current, {
        fieldId,
        fieldType: 'CUSTOM',
        customFieldType: 'ADDNEWMODAL',
      });
    }
  };

  const onDialogClose = useCallback(() => {
    props.onClose?.();
  }, [props]);

  const onSubmit = (values: FieldValues) => {
    //If buttonType='submit' and ref is in use then onSubmit will be called twice
    if (!isSubmitted) {
      const value = values.value;
      addValue(value);

      onDialogClose();
    }
  };

  const onFormSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(onSubmit)(e);
  };

  return (
    <>
      {modalProps.visible && (
        <Dialog
          {...modalProps}
          ref={ref}
          onClose={onDialogClose}
          buttonType="submit"
          buttons={[button]}
          containerClassName="md:w-[340px]"
        >
          <form onSubmit={onFormSubmit} id={formId}>
            <HfInput
              type="text"
              label={t('new-item-name')}
              placeholder={placeholder}
              control={control}
              name="value"
              error={errors.value?.message as string}
              onFocus={onFocus}
              onChangeEvent={onChangeEvent}
              debounceTime={500}
              readOnly={false}
              required={DynamicFormRequiredType.True}
            />
            {helpText && <span className="text-primary-1">{helpText}</span>}
          </form>
        </Dialog>
      )}
    </>
  );
}

export default AddNewModal;
